import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type ProviderLiveConnectionStatus = 'SUCCEEDED' | 'FAILED' | 'UNKNOWN' | 'NOT_CONFIGURED';
export type ProviderLiveConnectionFailureReason =
  | 'AUTHENTICATION_UNAVAILABLE'
  | 'AUTHENTICATION_DENIED'
  | 'TIMEOUT'
  | 'CONNECTION_FAILED'
  | 'INVALID_RESPONSE'
  | 'PROJECT_SCOPE_MISMATCH'
  | 'REDIRECT_REJECTED'
  | 'ENVIRONMENT_UNSUPPORTED'
  | 'READ_CAPABILITY_UNAVAILABLE';

export interface ProviderLiveConnection {
  property_id: string;
  environment: 'dev' | 'prod';
  provider: 'EZREMS';
  scope: 'PROJECT_ROOM_READ';
  status: ProviderLiveConnectionStatus;
  source: 'LIVE_EZREMS_ROOM_LIST';
  project_mapping_configured: boolean;
  observed_at: string | null;
  failure_reason: ProviderLiveConnectionFailureReason | null;
}

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const keys = [
  'property_id',
  'environment',
  'provider',
  'scope',
  'status',
  'source',
  'project_mapping_configured',
  'observed_at',
  'failure_reason',
] as const;
const statuses = new Set<ProviderLiveConnectionStatus>([
  'SUCCEEDED',
  'FAILED',
  'UNKNOWN',
  'NOT_CONFIGURED',
]);
const failureReasons = new Set<ProviderLiveConnectionFailureReason>([
  'AUTHENTICATION_UNAVAILABLE',
  'AUTHENTICATION_DENIED',
  'TIMEOUT',
  'CONNECTION_FAILED',
  'INVALID_RESPONSE',
  'PROJECT_SCOPE_MISMATCH',
  'REDIRECT_REJECTED',
  'ENVIRONMENT_UNSUPPORTED',
  'READ_CAPABILITY_UNAVAILABLE',
]);
const attemptedReadFailureReasons = new Set<ProviderLiveConnectionFailureReason>([
  'AUTHENTICATION_UNAVAILABLE',
  'AUTHENTICATION_DENIED',
  'TIMEOUT',
  'CONNECTION_FAILED',
  'INVALID_RESPONSE',
  'PROJECT_SCOPE_MISMATCH',
  'REDIRECT_REJECTED',
]);
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '현재 프로젝트 객실 조회 결과 형식이 올바르지 않습니다.',
  });

function valid(value: unknown, propertyId: string): value is ProviderLiveConnection {
  if (
    !record(value) ||
    Object.keys(value).length !== keys.length ||
    !keys.every((key) => Object.prototype.hasOwnProperty.call(value, key)) ||
    !isUuid(value.property_id) ||
    value.property_id.toLowerCase() !== propertyId.toLowerCase() ||
    !['dev', 'prod'].includes(value.environment as string) ||
    value.provider !== 'EZREMS' ||
    value.scope !== 'PROJECT_ROOM_READ' ||
    !statuses.has(value.status as ProviderLiveConnectionStatus) ||
    value.source !== 'LIVE_EZREMS_ROOM_LIST' ||
    typeof value.project_mapping_configured !== 'boolean' ||
    !(value.observed_at === null || isOffsetDateTime(value.observed_at)) ||
    !(
      value.failure_reason === null ||
      failureReasons.has(value.failure_reason as ProviderLiveConnectionFailureReason)
    )
  )
    return false;

  switch (value.status) {
    case 'SUCCEEDED':
      return (
        value.project_mapping_configured &&
        value.observed_at !== null &&
        value.failure_reason === null
      );
    case 'FAILED':
      return (
        value.project_mapping_configured &&
        value.observed_at !== null &&
        value.failure_reason !== null &&
        attemptedReadFailureReasons.has(value.failure_reason as ProviderLiveConnectionFailureReason)
      );
    case 'UNKNOWN':
      return (
        value.project_mapping_configured &&
        value.observed_at === null &&
        (value.failure_reason === 'ENVIRONMENT_UNSUPPORTED' ||
          value.failure_reason === 'READ_CAPABILITY_UNAVAILABLE')
      );
    case 'NOT_CONFIGURED':
      return (
        !value.project_mapping_configured &&
        value.observed_at === null &&
        value.failure_reason === null
      );
    default:
      return false;
  }
}

export async function readProviderLiveConnection(
  client: ServiceClient,
  propertyId: string,
  signal?: AbortSignal,
): Promise<ProviderLiveConnection> {
  if (!isUuid(propertyId)) throw invalid();
  const body = await client.get<unknown>(
    `admin/properties/${propertyId}/provider-live-connection`,
    { signal, timeout: 35_000, headers: { 'Cache-Control': 'no-store' } },
  );
  if (!valid(body, propertyId)) throw invalid();
  return body;
}
